import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, ViewEncapsulation } from '@angular/core';
import { AbstractControl, FormControl } from '@angular/forms';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { MatDialog } from '@angular/material/dialog';
import { Actions, Store, ofActionDispatched } from '@ngxs/store';
import { CreateCertificationProviderSuccess } from 'app/library/certification-provider/actions';
import { CertificationProviderStatusEnum } from 'app/library/certification-provider/enums/certification-provider-status.enum';
import { CertificationProviderTypeEnum } from 'app/library/certification-provider/enums/certification-provider-type.enum';
import { CertificationProvider } from 'app/library/certification-provider/models/certification-provider';
import { CertificationProviderService } from 'app/library/certification-provider/services/certification-provider.service';
import { CreateCertificationFailure, CreateCertificationSuccess } from 'app/library/certifications/actions';
import { ICreateAutocompleteOption } from 'app/library/certifications/interfaces/create-autocomplete-option.interface';
import { Certification } from 'app/library/certifications/models/certification';
import { CertificationService } from 'app/library/certifications/services/certification.service';
import { AdminPermissions } from 'app/library/feature/enums/admin-permissions.enum';
import { UnsubscribeOnDestroy } from 'app/projects/core/src/lib/models/unsubscribe-on-destroy';
import { NameToggleDialogComponent } from 'app/projects/shared/src/lib/components/dialogs/name-toggle/name-toggle.component';
import { UserState } from 'app/projects/user/src/lib/models/user.state';
import { debounceTime, distinctUntilChanged, take, takeUntil } from 'rxjs/operators';

@Component({
    selector: 'app-certificate-taxonomy-fields',
    templateUrl: './certificate-taxonomy-fields.component.html',
    styleUrls: ['./certificate-taxonomy-fields.component.scss'],
    encapsulation: ViewEncapsulation.None,
})
export class CertificateTaxonomyFieldsComponent extends UnsubscribeOnDestroy implements OnInit, OnChanges {
    @Input()
    providerControl: AbstractControl;

    @Input()
    certificationControl: AbstractControl;

    @Input()
    disabled = false;

    @Input()
    allowCreate = true;

    @Input()
    initialProvider: CertificationProvider | null = null;

    @Input()
    initialCertification: Certification | null = null;

    @Output()
    providerChange = new EventEmitter<CertificationProvider>();

    @Output()
    certificationChange = new EventEmitter<Certification>();

    providerInputCtrl = new FormControl('');
    certificationInputCtrl = new FormControl({ value: '', disabled: true });

    providers: CertificationProvider[] = [];
    certifications: Certification[] = [];

    showCreateProvider = false;
    showCreateCertification = false;
    createProviderName = '';
    createCertificationName = '';

    isCreatingProvider = false;
    isCreatingCertification = false;

    private _canCreateAsAdmin = false;
    private _selectedProvider: CertificationProvider | null = null;
    private _selectedCertification: Certification | null = null;
    private _providerSearchRequestId = 0;
    private _certificationSearchRequestId = 0;

    constructor(
        private _certificationProviderService: CertificationProviderService,
        private _certificationService: CertificationService,
        private _matDialog: MatDialog,
        private _actions$: Actions,
        private _store: Store
    ) {
        super();
    }

    ngOnInit(): void {
        const profile = this._store.selectSnapshot(UserState.getMyInfo);
        this._canCreateAsAdmin =
            profile.permissions.includes(AdminPermissions.CERTIFICATION_WRITE) ||
            profile.permissions.includes(AdminPermissions.TENANT_MANAGEMENT);

        if (this.disabled) {
            this.providerInputCtrl.disable({ emitEvent: false });
            this.certificationInputCtrl.disable({ emitEvent: false });
        }

        this.providerInputCtrl.valueChanges
            .pipe(debounceTime(300), distinctUntilChanged(), takeUntil(this._unsubscribeAll))
            .subscribe((value) => this.onProviderInputChanged(value));

        this.certificationInputCtrl.valueChanges
            .pipe(debounceTime(300), distinctUntilChanged(), takeUntil(this._unsubscribeAll))
            .subscribe((value) => this.onCertificationInputChanged(value));

        void this.initializeFields();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.disabled && !changes.disabled.firstChange) {
            if (this.disabled) {
                this.providerInputCtrl.disable({ emitEvent: false });
                this.certificationInputCtrl.disable({ emitEvent: false });
            } else {
                this.providerInputCtrl.enable({ emitEvent: false });
                if (this._selectedProvider) {
                    this.certificationInputCtrl.enable({ emitEvent: false });
                }
            }
        }
    }

    displayProviderName = (value: CertificationProvider | ICreateAutocompleteOption | string | null): string => {
        if (!value) {
            return '';
        }

        if (typeof value === 'string') {
            return value;
        }

        if (this.isCreateOption(value)) {
            return value.name;
        }

        return value.providerName;
    };

    displayCertificationName = (value: Certification | ICreateAutocompleteOption | string | null): string => {
        if (!value) {
            return '';
        }

        if (typeof value === 'string') {
            return value;
        }

        if (this.isCreateOption(value)) {
            return value.name;
        }

        return value.certificationName;
    };

    async onProviderOptionSelected(event: MatAutocompleteSelectedEvent): Promise<void> {
        const value = event.option.value as CertificationProvider | ICreateAutocompleteOption;

        if (this.isCreateOption(value)) {
            await this.createProvider(value.name);
            return;
        }

        this.selectProvider(value);
    }

    async onCertificationOptionSelected(event: MatAutocompleteSelectedEvent): Promise<void> {
        const value = event.option.value as Certification | ICreateAutocompleteOption;

        if (this.isCreateOption(value)) {
            this.openCreateCertificationDialog(value.name);
            return;
        }

        this.selectCertification(value);
    }

    async selectProviderById(providerId: string): Promise<void> {
        if (!providerId) {
            return;
        }

        let provider = this.providers.find((item) => item.id === providerId) || null;

        if (!provider && this.initialProvider?.id === providerId) {
            provider = this.initialProvider;
        }

        if (!provider) {
            await this.searchProviders('');
            provider = this.providers.find((item) => item.id === providerId) || null;
        }

        if (provider) {
            this.selectProvider(provider);
            await this.searchCertifications('');
        }
    }

    async selectCertificationById(certificationId: string): Promise<void> {
        if (!certificationId || !this._selectedProvider) {
            return;
        }

        let certification = this.certifications.find((item) => item.id === certificationId) || null;

        if (!certification && this.initialCertification?.id === certificationId) {
            certification = this.initialCertification;
        }

        if (!certification) {
            await this.searchCertifications('');
            certification = this.certifications.find((item) => item.id === certificationId) || null;
        }

        if (certification) {
            this.selectCertification(certification);
        }
    }

    private async initializeFields(): Promise<void> {
        await this.searchProviders('');

        const providerId = this.providerControl?.value || this.initialProvider?.id;
        if (providerId) {
            await this.selectProviderById(providerId);

            const certificationId = this.certificationControl?.value || this.initialCertification?.id;
            if (certificationId) {
                await this.selectCertificationById(certificationId);
            }
        }
    }

    private async onProviderInputChanged(value: CertificationProvider | ICreateAutocompleteOption | string | null): Promise<void> {
        if (this.isCreatingProvider) {
            return;
        }

        if (value && typeof value === 'object' && !this.isCreateOption(value)) {
            this.updateCreateProviderVisibility('');
            return;
        }

        const search = typeof value === 'string' ? value.trim() : this.isCreateOption(value) ? value.name.trim() : '';

        if (this._selectedProvider && search !== this._selectedProvider.providerName) {
            this.clearProviderSelection(false);
        }

        this.updateCreateProviderVisibility(search);
        await this.searchProviders(search);
    }

    private async onCertificationInputChanged(value: Certification | ICreateAutocompleteOption | string | null): Promise<void> {
        if (this.isCreatingCertification || !this._selectedProvider) {
            return;
        }

        if (value && typeof value === 'object' && !this.isCreateOption(value)) {
            this.updateCreateCertificationVisibility('');
            return;
        }

        const search = typeof value === 'string' ? value.trim() : this.isCreateOption(value) ? value.name.trim() : '';

        if (this._selectedCertification && search !== this._selectedCertification.certificationName) {
            this.clearCertificationSelection(false);
        }

        this.updateCreateCertificationVisibility(search);
        await this.searchCertifications(search);
    }

    private async searchProviders(search: string): Promise<void> {
        const requestId = ++this._providerSearchRequestId;

        await this._certificationProviderService.fetchCertificationProviders(
            100,
            1,
            search,
            [CertificationProviderTypeEnum.External, CertificationProviderTypeEnum.Other],
            [CertificationProviderStatusEnum.Active]
        );

        if (requestId !== this._providerSearchRequestId) {
            return;
        }

        let providers = this._certificationProviderService.getCertificationProviders() || [];

        if (this.initialProvider && !providers.some((provider) => provider.id === this.initialProvider.id)) {
            providers = [this.initialProvider, ...providers];
        }

        if (this._selectedProvider && !providers.some((provider) => provider.id === this._selectedProvider.id)) {
            providers = [this._selectedProvider, ...providers];
        }

        this.providers = providers;
        this.updateCreateProviderVisibility(typeof this.providerInputCtrl.value === 'string' ? this.providerInputCtrl.value.trim() : this.createProviderName);
    }

    private async searchCertifications(search: string): Promise<void> {
        if (!this._selectedProvider) {
            this.certifications = [];
            return;
        }

        const requestId = ++this._certificationSearchRequestId;

        await this._certificationService.fetchCertifications(100, 1, search, [this._selectedProvider.id], [CertificationProviderStatusEnum.Active]);

        if (requestId !== this._certificationSearchRequestId) {
            return;
        }

        let certifications = (this._certificationService.getCertifications() || []).filter(
            (certification) =>
                certification.status === CertificationProviderStatusEnum.Active ||
                certification.id === this.initialCertification?.id ||
                certification.id === this._selectedCertification?.id
        );

        if (this.initialCertification && this.initialCertification.provider?.id === this._selectedProvider.id && !certifications.some((item) => item.id === this.initialCertification.id)) {
            certifications = [this.initialCertification, ...certifications];
        }

        if (this._selectedCertification && !certifications.some((item) => item.id === this._selectedCertification.id)) {
            certifications = [this._selectedCertification, ...certifications];
        }

        this.certifications = certifications;
        this.updateCreateCertificationVisibility(
            typeof this.certificationInputCtrl.value === 'string' ? this.certificationInputCtrl.value.trim() : this.createCertificationName
        );
    }

    private selectProvider(provider: CertificationProvider): void {
        this._selectedProvider = provider;
        this.providerInputCtrl.setValue(provider.providerName, { emitEvent: false });

        if (!this.disabled) {
            this.providerControl?.setValue(provider.id);
            this.providerControl?.markAsDirty();
            this.providerControl?.markAsTouched();
        }

        this.clearCertificationSelection(true);

        if (this.disabled) {
            this.providerInputCtrl.disable({ emitEvent: false });
            this.certificationInputCtrl.disable({ emitEvent: false });
        } else {
            this.certificationInputCtrl.enable({ emitEvent: false });
            this.certificationControl?.enable({ emitEvent: false });
        }

        this.showCreateProvider = false;
        this.createProviderName = '';

        if (!this.disabled) {
            this.providerChange.emit(provider);
        }

        void this.searchCertifications('');
    }

    private selectCertification(certification: Certification): void {
        this._selectedCertification = certification;
        this.certificationInputCtrl.setValue(certification.certificationName, { emitEvent: false });

        if (this.disabled) {
            this.certificationInputCtrl.disable({ emitEvent: false });
        } else {
            this.certificationControl?.setValue(certification.id);
            this.certificationControl?.markAsDirty();
            this.certificationControl?.markAsTouched();
            this.certificationChange.emit(certification);
        }

        this.showCreateCertification = false;
        this.createCertificationName = '';
    }

    private clearProviderSelection(resetInput: boolean): void {
        this._selectedProvider = null;

        if (!this.disabled) {
            this.providerControl?.setValue(null);
        }

        if (resetInput) {
            this.providerInputCtrl.setValue('', { emitEvent: false });
        }

        this.clearCertificationSelection(true);
        this.certificationInputCtrl.disable({ emitEvent: false });

        if (!this.disabled) {
            this.certificationControl?.disable({ emitEvent: false });
        }
    }

    private clearCertificationSelection(resetInput: boolean): void {
        this._selectedCertification = null;
        this.showCreateCertification = false;
        this.createCertificationName = '';

        if (!this.disabled) {
            this.certificationControl?.setValue(null);
        }

        if (resetInput) {
            this.certificationInputCtrl.setValue('', { emitEvent: false });
        }
    }

    private updateCreateProviderVisibility(search: string): void {
        const trimmed = (search || '').trim();
        const exactMatch = this.providers.some((provider) => provider.providerName?.toLowerCase() === trimmed.toLowerCase());

        this.createProviderName = trimmed;
        this.showCreateProvider = this.isCreateAllowed && !this.disabled && !!trimmed && !exactMatch;
    }

    private updateCreateCertificationVisibility(search: string): void {
        const trimmed = (search || '').trim();
        const exactMatch = this.certifications.some((certification) => certification.certificationName?.toLowerCase() === trimmed.toLowerCase());

        this.createCertificationName = trimmed;
        this.showCreateCertification = this.isCreateAllowed && !this.disabled && !!this._selectedProvider && !!trimmed && !exactMatch;
    }

    private get isCreateAllowed(): boolean {
        return this.allowCreate && this._canCreateAsAdmin;
    }

    private async createProvider(name: string): Promise<void> {
        const trimmedName = name.trim();
        if (!trimmedName || this.isCreatingProvider) {
            return;
        }

        this.isCreatingProvider = true;

        let createdProvider: CertificationProvider | null = null;

        this._actions$
            .pipe(ofActionDispatched(CreateCertificationProviderSuccess), take(1), takeUntil(this._unsubscribeAll))
            .subscribe((action: CreateCertificationProviderSuccess) => {
                createdProvider = action.payload;
            });

        const success = await this._certificationProviderService.createCertificationProvider(trimmedName, CertificationProviderTypeEnum.External);
        this.isCreatingProvider = false;

        if (success && createdProvider) {
            this.selectProvider(createdProvider);
            return;
        }

        this.providerInputCtrl.setValue(trimmedName, { emitEvent: false });
        this.updateCreateProviderVisibility(trimmedName);
    }

    private openCreateCertificationDialog(name: string): void {
        if (!this._selectedProvider || this.isCreatingCertification) {
            return;
        }

        const dialogRef = this._matDialog.open(NameToggleDialogComponent, {
            width: '630px',
        });

        dialogRef.componentInstance.dialogTitle = 'PAGE.CERTIFICATIONS.BUTTON.ADD';
        dialogRef.componentInstance.placeholder = 'PAGE.CERTIFICATIONS.LABEL.NAME';
        dialogRef.componentInstance.buttonLabel = 'GENERAL.BUTTON.ADD';
        dialogRef.componentInstance.toggleLabel = 'GENERAL.LABEL.CERTIFICATE_EXPIRATION';
        dialogRef.componentInstance.name = name;

        dialogRef.componentInstance.save.pipe(takeUntil(this._unsubscribeAll)).subscribe(async (result) => {
            if (!result?.name || this.isCreatingCertification) {
                return;
            }

            this.isCreatingCertification = true;
            dialogRef.componentInstance.showSpinner = true;

            let createdCertification: Certification | null = null;

            this._actions$
                .pipe(ofActionDispatched(CreateCertificationSuccess), take(1), takeUntil(this._unsubscribeAll))
                .subscribe((action: CreateCertificationSuccess) => {
                    createdCertification = action.payload;
                });

            this._actions$.pipe(ofActionDispatched(CreateCertificationFailure), take(1)).subscribe((action: CreateCertificationFailure) => {
                dialogRef.componentInstance.errors.next({ message: action.payload.response.error.errors.name });
            });

            const success = await this._certificationService.createCertification(
                result.name,
                this._selectedProvider.id,
                result.toggleCtrl,
                result.targetCount
            );

            this.isCreatingCertification = false;
            dialogRef.componentInstance.showSpinner = false;

            if (success && createdCertification) {
                dialogRef.close();
                this.selectCertification(createdCertification);
                return;
            }
        });

        dialogRef.afterClosed().subscribe(() => {
            if (!this._selectedCertification) {
                this.certificationInputCtrl.setValue(name, { emitEvent: false });
                this.updateCreateCertificationVisibility(name);
            }
        });
    }

    private isCreateOption(value: unknown): value is ICreateAutocompleteOption {
        return !!value && typeof value === 'object' && (value as ICreateAutocompleteOption).__create === true;
    }

    get createProviderOption(): ICreateAutocompleteOption {
        return { __create: true, name: this.createProviderName };
    }

    get createCertificationOption(): ICreateAutocompleteOption {
        return { __create: true, name: this.createCertificationName };
    }
}
